// src/pages/Settings.js
import React, { useEffect, useMemo, useState, Suspense } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
} from '@chakra-ui/react';

import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AccountDetails from './settings/AccountDetails';
import SettingsTemplates from './settings/Templates';

// correct path (pages -> components)
const PriceEditor = React.lazy(() => import('../components/PriceEditor'));

export default function Settings({ section = 'branding' }) {
  const toast = useToast();

  // tenancy / auth
  const [authReady, setAuthReady] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [orgSource, setOrgSource] = useState(null); // 'claims' | 'adminsDoc' | null

  // ---------------- BRANDING (company name only) ----------------
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [companyName, setCompanyName] = useState('');

  // ---------------- ACCOUNT (legacy state kept for backwards compatibility) ----------------
  const [loadingBiz, setLoadingBiz] = useState(false);
  const [savingBiz, setSavingBiz] = useState(false);
  const [businessType, setBusinessType] = useState('sole'); // 'sole' | 'ltd'
  const [tradingName, setTradingName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [vatNo, setVatNo] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [tradingAddress, setTradingAddress] = useState('');
  const [registeredCompanyName, setRegisteredCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');

  // ---------------- ROLES (members list) ------------------------
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const addModal = useDisclosure();
  const pwdModal = useDisclosure();

  // edit modal
  const editModal = useDisclosure();
  const [editTargetId, setEditTargetId] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin'); // 'admin' | 'superadmin'

  // --------------------------------------------------------------
  // Resolve orgId:
  useEffect(() => {
    const auth = getAuth();
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setOrgId(null);
        setOrgSource(null);
        setAuthReady(true);
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        const claimOrg = token?.claims?.orgId;
        if (typeof claimOrg === 'string' && claimOrg.length > 0) {
          setOrgId(claimOrg);
          setOrgSource('claims');
          setAuthReady(true);
          return;
        }
        const adminSnap = await getDoc(doc(db, 'admins', user.uid));
        const adminOrg = adminSnap.exists() ? adminSnap.data()?.orgId : null;
        if (typeof adminOrg === 'string' && adminOrg.length > 0) {
          setOrgId(adminOrg);
          setOrgSource('adminsDoc');
        } else {
          setOrgId(null);
          setOrgSource(null);
        }
      } catch (e) {
        console.warn('[Settings] org resolution failed:', e);
        setOrgId(null);
        setOrgSource(null);
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  // ---------------- Load BRANDING ----------------
  useEffect(() => {
    if (section !== 'branding' || !orgId) return;
    const run = async () => {
      setLoadingBrand(true);
      try {
        const orgRef = doc(db, 'organizations', orgId);
        const snap = await getDoc(orgRef);
        setCompanyName(snap.exists() ? snap.data().companyName || '' : '');
      } catch (err) {
        toast({
          title: 'Failed to load',
          description: String(err.message || err),
          status: 'error',
        });
      } finally {
        setLoadingBrand(false);
      }
    };
    run();
  }, [section, orgId, toast]);

  const handleSaveBranding = async () => {
    if (!orgId) {
      toast({ title: 'Organisation not set', status: 'warning' });
      return;
    }
    setSavingBrand(true);
    try {
      await setDoc(
        doc(db, 'organizations', orgId),
        { companyName: companyName || '', updatedAt: serverTimestamp() },
        { merge: true }
      );
      toast({ title: 'Saved', status: 'success' });
    } catch (err) {
      toast({ title: 'Save failed', description: String(err.message || err), status: 'error' });
    } finally {
      setSavingBrand(false);
    }
  };

  // ---------------- Functions clients (kept as-is) ----------------
  const fn = getFunctions(undefined, 'europe-west2');
  const callDeleteOrg = httpsCallable(fn, 'deleteOrganization');
  const callPurgeOrg = httpsCallable(fn, 'permanentlyDeleteOrganization');
  const callCreate = httpsCallable(fn, 'createUserForOrg');
  const callSetRole = httpsCallable(fn, 'setUserRoleForOrg');
  const callSuspend = httpsCallable(fn, 'suspendMember');
  const callResetPwd = httpsCallable(fn, 'resetPasswordForOrgMember');
  const callRemove = httpsCallable(fn, 'removeMemberFromOrg');
  const callMigrate = httpsCallable(fn, 'migrateAdminsToMembers');
  const callUpdateMember = httpsCallable(fn, 'updateMemberProfileForOrg');

  // ---------------- tenancy / header badge ----------------------
  const tenancyNotice = useMemo(() => {
    if (!authReady) return null;
    if (!orgId) {
      return (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Organisation not assigned</AlertTitle>
            <AlertDescription>
              Your account has no <code>orgId</code>. Ask a super-admin to add an orgId claim or
              create <code>/admins/&lt;uid&gt;</code> with an <code>orgId</code> field.
            </AlertDescription>
          </Box>
        </Alert>
      );
    }
    if (orgSource === 'adminsDoc') {
      return (
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Temporary organisation mapping</AlertTitle>
            <AlertDescription>
              Using <code>/admins/&lt;uid&gt;.orgId</code>. For production, set the auth custom
              claim <code>orgId</code>.
            </AlertDescription>
          </Box>
          {/* orgId pill removed */}
        </Alert>
      );
    }
    return null;
  }, [authReady, orgId, orgSource]);

  if (!authReady) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      {tenancyNotice}

      {/* BRANDING */}
      {section === 'branding' && (
        <Box borderWidth={1} borderRadius="md" p={4} mt={2}>
          <Heading size="md" mb={3}>
            Branding
          </Heading>
          {!orgId ? (
            <Text fontSize="sm" color="gray.500">
              An organisation is required to edit branding.
            </Text>
          ) : loadingBrand ? (
            <Spinner />
          ) : (
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Company name</FormLabel>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your company name"
                />
              </FormControl>
              <HStack>
                <Button colorScheme="blue" onClick={handleSaveBranding} isLoading={savingBrand}>
                  Save
                </Button>
              </HStack>
            </VStack>
          )}
        </Box>
      )}

      {/* ACCOUNT */}
      {section === 'account' && <AccountDetails />}

      {/* PRICE LIST */}
      {section === 'prices' && (
        <Box borderWidth={1} borderRadius="md" p={4} mt={4}>
          <Heading size="md" mb={2}>
            Price List
          </Heading>
          {!orgId ? (
            <Text fontSize="sm" color="gray.500">
              An organisation is required to edit price lists.
            </Text>
          ) : (
            <>
              <Text fontSize="sm" color="gray.600" mb={2}>
                For Doors pricing use the "Manage Doors" under Settings.
              </Text>
              <Suspense fallback={<Spinner />}>
                <PriceEditor />
              </Suspense>
            </>
          )}
        </Box>
      )}

      {/* ROLES (unchanged) */}
      {/* ... */}

      {/* Templates */}
      {section === 'templates' && <SettingsTemplates />}
    </Box>
  );
}
